import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Paper, Typography } from '@material-ui/core';
import { Edit } from '@material-ui/icons';

/**
 * @function ShapeEditControl
 * @description Renders the "Edit points" toggle (and the error raised by an invalid edit) into the
 * map control that `useShapeVertexEditing` mounts, so the button sits on the map instead of around
 * it. Renders nothing until there is a shape to edit.
 * @param {Object} props - Component properties.
 * @param {Object} props.editing - The object returned by `useShapeVertexEditing`.
 * @param {Boolean} [props.visible] - Hides the control while false, e.g. before anything is drawn.
 * @see {@link useShapeVertexEditing}
 * **Called by:
 * @see {AOIChooser} - each module's area-of-interest step.
 * @returns {React.ReactPortal|null} The control, or null while there is nothing to edit.
 */
const ShapeEditControl = ({ editing, visible = true }) => {
  const [t] = useTranslation();
  const { controlContainer, hasShape, isEditing, geometryError, toggleEdit } = editing;

  if (!visible || !controlContainer || !hasShape) return null;

  return ReactDOM.createPortal(
    <Paper elevation={3} style={{ padding: 8, maxWidth: 260, textAlign: 'center' }}>
      <Button
        onClick={toggleEdit}
        startIcon={<Edit />}
        color="primary"
        size="small"
        variant={isEditing ? 'contained' : 'outlined'}>
        {isEditing ? t('forms.map.finishEdit') : t('forms.map.edit')}
      </Button>
      {geometryError !== '' && (
        <Typography variant="caption" display="block" style={{ marginTop: 6, color: '#c62828' }}>
          {geometryError}
        </Typography>
      )}
    </Paper>,
    controlContainer,
  );
};

export default ShapeEditControl;
